/**
 * DRR-123: Information Barrier Wall Indicator
 *
 * Shows the active wall (clinical/operational) and allows switching.
 * Switching walls requires re-authentication per DRR-123 dual-role policy.
 *
 * Dev mode: Reads/writes `dev_active_wall` in localStorage.
 * Prod mode: Wall derived from JWT `ferp_active_role` claim via backend classify_wall().
 */
import { Badge, Menu, UnstyledButton, Group, Text, Modal, Stack, Button, Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconShieldLock, IconChevronDown, IconAlertTriangle } from '@tabler/icons-react';
import { useState, useEffect, useCallback } from 'react';
import { isDevModeEnabled } from '@/utils/env';

type WallName = 'clinical' | 'operational' | '';

const wallConfig: Record<string, { label: string; color: string; description: string }> = {
  clinical: {
    label: 'Clinical',
    color: 'teal',
    description: 'Clinical decision-making wall. Billing/claims/accounting data is blocked.',
  },
  operational: {
    label: 'Operational',
    color: 'indigo',
    description: 'Facility operations wall. Clinical write/medication data is blocked.',
  },
  '': {
    label: 'No Wall',
    color: 'gray',
    description: 'No information barrier active. All data visible.',
  },
};

export function WallIndicator() {
  const [activeWall, setActiveWall] = useState<WallName>('');
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [pendingWall, setPendingWall] = useState<WallName>('');

  useEffect(() => {
    const stored = localStorage.getItem('dev_active_wall') || '';
    setActiveWall(stored as WallName);
  }, []);

  const handleWallSwitch = useCallback((wall: WallName) => {
    if (wall === activeWall) return;
    setPendingWall(wall);
    openConfirm();
  }, [activeWall, openConfirm]);

  const confirmSwitch = useCallback(() => {
    if (pendingWall !== undefined) {
      localStorage.setItem('dev_active_wall', pendingWall);
      setActiveWall(pendingWall);
      closeConfirm();
      // Reload to re-initialize Apollo Client with new wall header
      window.location.reload();
    }
  }, [pendingWall, closeConfirm]);

  // Only show in dev mode (prod uses JWT claim)
  if (!isDevModeEnabled()) return null;

  // wallConfig[''] is always defined — fallback is safe.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const config = (wallConfig[activeWall] ?? wallConfig[''])!;

  return (
    <>
      <Menu shadow="md" width={280}>
        <Menu.Target>
          <UnstyledButton>
            <Group gap="xs">
              <IconShieldLock size={16} />
              <Badge color={config.color} variant="light" size="sm">
                {config.label} Wall
              </Badge>
              <IconChevronDown size={14} />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>DRR-123: Information Barrier</Menu.Label>
          <Menu.Item
            onClick={() => handleWallSwitch('clinical')}
            color={activeWall === 'clinical' ? 'teal' : undefined}
            leftSection={<IconShieldLock size={14} />}
          >
            <Text size="sm">Clinical Wall</Text>
            <Text size="xs" c="dimmed">Blocks billing/claims/accounting</Text>
          </Menu.Item>
          <Menu.Item
            onClick={() => handleWallSwitch('operational')}
            color={activeWall === 'operational' ? 'indigo' : undefined}
            leftSection={<IconShieldLock size={14} />}
          >
            <Text size="sm">Operational Wall</Text>
            <Text size="xs" c="dimmed">Blocks clinical write/medication</Text>
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            onClick={() => handleWallSwitch('')}
            color={activeWall === '' ? 'gray' : undefined}
          >
            <Text size="sm">No Wall (Architect)</Text>
            <Text size="xs" c="dimmed">All data visible — dev only</Text>
          </Menu.Item>
          {activeWall && (
            <>
              <Menu.Divider />
              <Menu.Label>Emergency</Menu.Label>
              <Menu.Item
                color="red"
                leftSection={<IconAlertTriangle size={14} />}
                disabled
              >
                <Text size="sm">Break Glass Override</Text>
                <Text size="xs" c="dimmed">
                  Requires fresh witness + 24h review (DRR-123)
                </Text>
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title="Switch Information Barrier"
        centered
      >
        <Stack>
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            DRR-123: Switching walls requires re-authentication in production.
            This creates a new session with a separate audit trail.
          </Alert>
          <Text size="sm">
            Switching from <Badge color={wallConfig[activeWall]?.color || 'gray'} variant="light">{wallConfig[activeWall]?.label || 'None'}</Badge>
            {' '}to{' '}
            <Badge color={wallConfig[pendingWall]?.color || 'gray'} variant="light">{wallConfig[pendingWall]?.label || 'None'}</Badge>
          </Text>
          <Text size="xs" c="dimmed">
            {wallConfig[pendingWall]?.description}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeConfirm}>Cancel</Button>
            <Button color="red" onClick={confirmSwitch}>
              Switch Wall (Dev Mode)
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
